import { USER_TYPES } from '../../services/user';
import { COMPANY_TYPES } from '../../services/vendor';

/** Standalone sub-nav sections, in display order, that each get their own screen and route. */
export const STANDALONE_SECTION_IDS = ['sites', 'applications', 'certifications', 'assessments', 'documents'] as const;

/** Company inner-tab keys, in display order (rendered under the Company item). */
export const COMPANY_INNER_TAB_KEYS = ['users', 'services', 'subsidiaries', 'pending-users'] as const;

/**
 * Standalone sub-nav section ids visible for the given company + viewer. Mirrors
 * the v1 accordion gating (ProfileV1.tsx): Sites/Applications/Certifications are
 * Service-Provider-only; Assessments shows for Vendor/Assessor companies but not
 * to a Content Owner viewer; Documents shows for any non-Content-Owner company.
 */
export function visibleStandaloneSectionIds(companyType?: string, userType?: string): string[] {
  const isVendor = companyType === COMPANY_TYPES.VENDOR;
  const isAssessorCompany = companyType === COMPANY_TYPES.ASSESSOR;
  const isContentOwnerCompany = companyType === COMPANY_TYPES.CONTENT_OWNER;
  return STANDALONE_SECTION_IDS.filter((id) => {
    switch (id) {
      case 'sites':
      case 'applications':
      case 'certifications':
        return isVendor;
      case 'assessments':
        return (isVendor || isAssessorCompany) && userType !== USER_TYPES.CONTENT_OWNER;
      case 'documents':
        return !isContentOwnerCompany;
      default:
        return false;
    }
  });
}

/**
 * Company inner-tab keys visible for the given company + viewer. Mirrors the v1
 * accordion gating: Services is Service-Provider-only; Subsidiaries is TPN Admin
 * only; Users shows to a company admin of a Vendor/Content-Owner company or to
 * any TPN Admin; Pending Users only when Users is visible and there are pending users.
 */
export function visibleCompanyInnerTabKeys(opts: {
  companyType?: string;
  userType?: string;
  userCanAdminCompany: boolean;
  pendingUserCount: number;
}): string[] {
  const { companyType, userType, userCanAdminCompany, pendingUserCount } = opts;
  const isTpnAdmin = userType === USER_TYPES.TPN_ADMIN;
  const isVendor = companyType === COMPANY_TYPES.VENDOR;
  const isContentOwnerCompany = companyType === COMPANY_TYPES.CONTENT_OWNER;
  const usersVisible =
    (isVendor && userCanAdminCompany) || isTpnAdmin || (isContentOwnerCompany && userCanAdminCompany);

  return COMPANY_INNER_TAB_KEYS.filter((key) => {
    switch (key) {
      case 'services':
        return isVendor;
      case 'subsidiaries':
        return isTpnAdmin;
      case 'users':
        return usersVisible;
      case 'pending-users':
        return usersVisible && pendingUserCount > 0;
      default:
        return false;
    }
  });
}

// Ordered to the TPN membership schedule (mirrors CompanyForm's grossRevenueOptions): level = bracket position.
const GROSS_REVENUE_LEVELS = [
  'Self-Employed',
  'Up to $2M',
  '$2 - 5M',
  '$5 - 10M',
  '$10 - 15M',
  '$15 - 25M',
  '$25 - 35M',
  '$35 - 50M',
  '$50 - 200M',
  '$200M+',
];

/** Service Provider membership level (1-10) for a gross-revenue bracket, or null when unknown. */
export function membershipLevel(grossRevenue?: string | null): number | null {
  if (!grossRevenue) return null;
  const index = GROSS_REVENUE_LEVELS.indexOf(grossRevenue.trim());
  return index === -1 ? null : index + 1;
}
